import type { Ref } from 'vue'

import type { VxeGridProps } from '@vben/plugins/vxe-table'

import { Checkbox as ACheckbox } from 'ant-design-vue'
import { get, isNil } from 'lodash-es'

// 轻量 checkbox 是 opt-in 能力，用外部 key 数组维护选中态，减少 500 条数据全选时的 vxe 内部状态压力。
export interface LightCheckboxSelectionOptions {
  type: 'checkbox'
  mode: 'light'
  model: Ref<Array<number | string>>
  keyField?: string
  onChange?: (keys: Array<number | string>) => void
  column?: Record<string, any>
}

// 只识别 selection: { type: 'checkbox', mode: 'light' }，不影响原生 checkbox 行为。
export function resolveLightCheckboxSelection(selection: unknown) {
  return typeof selection === 'object' &&
    selection !== null &&
    (selection as LightCheckboxSelectionOptions).type === 'checkbox' &&
    (selection as LightCheckboxSelectionOptions).mode === 'light'
    ? (selection as LightCheckboxSelectionOptions)
    : undefined
}

export function createLightCheckboxSelection<T>({
  getDefaultKeyField,
  getGridApi,
  selection,
}: {
  getDefaultKeyField: () => string
  getGridApi: () => any
  selection: LightCheckboxSelectionOptions
}) {
  const getKeyField = () => selection.keyField ?? getDefaultKeyField()
  const getColumnField = () => selection.column?.field ?? 'checkbox'
  // 当前页数据来自 grid 实例，跨页选中态只保存在 selection.model 中。
  const getRows = () => (getGridApi()?.grid?.getData?.() ?? []) as T[]
  const getKey = (row: T) =>
    get(row, getKeyField()) as number | string | undefined

  // 所有选中变更统一通过 setKeys 写回外部 model，方便业务继续复用原批量操作逻辑。
  const setKeys = (keys: Array<number | string>) => {
    selection.model.value = keys
    selection.onChange?.(keys)
  }

  const setRowChecked = (row: T, checked: boolean) => {
    const key = getKey(row)
    if (isNil(key)) {
      return
    }

    const nextKeySet = new Set(selection.model.value)
    if (checked) {
      nextKeySet.add(key)
    } else {
      nextKeySet.delete(key)
    }
    setKeys([...nextKeySet])
  }

  // 表头 checkbox 只切换当前页，避免误操作所有分页数据。
  const setPageChecked = (checked: boolean) => {
    const nextKeySet = new Set(selection.model.value)

    getRows().forEach((row) => {
      const key = getKey(row)
      if (isNil(key)) {
        return
      }

      if (checked) {
        nextKeySet.add(key)
      } else {
        nextKeySet.delete(key)
      }
    })

    setKeys([...nextKeySet])
  }

  // 根据当前页选中数量计算表头 checkbox 的全选/半选状态。
  const getPageCheckedState = () => {
    const rows = getRows()
    const selectedKeySet = new Set(selection.model.value)
    const checkedCount = rows.filter((row) => {
      const key = getKey(row)
      return !isNil(key) && selectedKeySet.has(key)
    }).length

    return {
      checked: rows.length > 0 && checkedCount === rows.length,
      indeterminate: checkedCount > 0 && checkedCount < rows.length,
    }
  }

  // 用 antd Checkbox 渲染选择列，绕开 vxe 原生 checkbox 的重状态同步。
  const createColumn = () => ({
    field: getColumnField(),
    width: 50,
    ...selection.column,
    slots: {
      ...selection.column?.slots,
      header: () => {
        const { checked, indeterminate } = getPageCheckedState()
        return (
          <ACheckbox
            checked={checked}
            indeterminate={indeterminate}
            onChange={(event) => setPageChecked(event.target.checked)}
          />
        )
      },
      default: ({ row }: { row: T }) => {
        const key = getKey(row)
        return (
          <ACheckbox
            checked={!isNil(key) && selection.model.value.includes(key)}
            onChange={(event) => setRowChecked(row, event.target.checked)}
          />
        )
      },
    },
  })

  // 动态列更新时保持轻量选择列在最前面，同时移除旧的 checkbox 列。
  const withSelectionColumn = (
    gridOptions: VxeGridProps<T>['gridOptions'] = {},
  ) => {
    if (!gridOptions.columns) {
      return gridOptions
    }

    const columnField = getColumnField()
    return {
      ...gridOptions,
      columns: [
        createColumn(),
        ...(gridOptions.columns ?? []).filter(
          (item: any) =>
            item?.field !== columnField && item?.type !== 'checkbox',
        ),
      ],
    }
  }

  // 初次创建 grid 时注入轻量选择列和 keyField。
  const applyGridOptions = (
    gridOptions: VxeGridProps<T>['gridOptions'] = {},
  ) => ({
    ...withSelectionColumn(gridOptions),
    checkboxConfig: {
      highlight: false,
      range: false,
      reserve: false,
      showReserveStatus: false,
      ...gridOptions.checkboxConfig,
    },
    rowConfig: {
      ...gridOptions.rowConfig,
      keyField: getKeyField(),
    },
  })

  // 业务动态更新 columns 时继续保留轻量选择列。
  const patchGridApi = (gridApi: any) => {
    const setGridOptions = gridApi.setGridOptions.bind(gridApi)
    gridApi.setGridOptions = (gridOptions: VxeGridProps<T>['gridOptions']) => {
      setGridOptions(withSelectionColumn(gridOptions))
    }
  }

  return {
    applyGridOptions,
    patchGridApi,
  }
}
